import React, { useEffect, useState } from 'react';
import { main, app } from '../state/actions';
import { connect } from 'react-redux';
import {
  getAvailableUnits,
  cmp,
  getTimelineMaxWidth,
  adjustSchedule,
  getThisWeekStartEnd,
  getNextWeekStartEnd,
  getLocatorUnit,
} from '../helpers/misc';
import {
  getDynamoDBClient,
  getUnitMoldingMachines,
  getUnitAssemblyMachines,
  getUnitReleasedJobs,
  getLocationStandards,
  getUnitScheduleRows,
  getToolsWithRequiredOperators,
  getToolsWithPmDates,
} from '../helpers/ddb';
import Toolbar from './Toolbar';
import GLOBAL_STYLES from '../styles';
import Timeline from './Timeline';
import TimelineLabel from './TimelineLabel';
import KeyboardEventHandler from 'react-keyboard-event-handler';
import EditSchedule from './EditSchedule';
import cloneDeep from 'lodash.clonedeep';


const mapStateToProps = state => ({
  credentials: state.main.credentials,
  colorScheme: state.main.colorScheme,
  windowDimensions: state.main.windowDimensions,
  scrollbarWidth: state.main.scrollbarWidth,
  authorizedModules: state.main.authorizedModules,
  machineData: state.app.machineData,
  locatorFilter: state.app.locatorFilter,
  locationData: state.app.locationData,
  timelineViewType: state.app.timelineViewType,
  timelineTextFilter: state.app.timelineTextFilter,
  weekFilter: state.app.weekFilter,
});

const mapDispatchToProps = dispatch => ({
  updateLastActivity: payload => dispatch(main.updateLastActivity(payload)),
  updateLoadingMessage: payload => dispatch(main.updateLoadingMessage(payload)),
  updateLocators: payload => dispatch(app.updateLocators(payload)),
  updateMachineData: payload => dispatch(app.updateMachineData(payload)),
  updateLocationData: payload => dispatch(app.updateLocationData(payload)),
});

const App = props => {
  const {
    logout,
    credentials,
    authorizedModules,
    updateLocators,
    updateMachineData,
    updateLoadingMessage,
    machineData,
    locatorFilter,
    windowDimensions,
    scrollbarWidth,
    colorScheme,
    updateLocationData,
    locationData,
    timelineViewType,
    timelineTextFilter,
    weekFilter,
  } = props;
  const [timelinePixelsPerHour, setTimelinePixelsPerHour] = useState(5);
  const [timeNow, setTimeNow] = useState(0);
  
  useEffect(() => {
    const initData = async () => {
      try {
        updateLoadingMessage('Initializing');
        const initTime = Date.now();
        const availableUnits = getAvailableUnits(authorizedModules);
        const ddb = getDynamoDBClient(credentials);
        // get presses per unit
        const allData = await Promise.all([
          Promise.all(availableUnits.map(async locator => (await Promise.all([
            getUnitMoldingMachines(ddb, locator),
            getUnitAssemblyMachines(ddb, locator),
          ])).flat())),
          Promise.all(availableUnits.map(locator => getUnitReleasedJobs(ddb, locator))),
          Promise.all(availableUnits.map(locator => getLocationStandards(ddb, locator))),
          Promise.all(availableUnits.map(locator => getUnitScheduleRows(ddb, locator))),
          availableUnits.includes('NAM-SLB-U40-P01') || availableUnits.includes('NAM-SLB-U40-P02') ||
          availableUnits.includes('NAM-SLB-U40-P03') ? getUnitReleasedJobs(ddb, 'NAM-SLB-U40') : [],
          Promise.all(availableUnits.map(locator => getToolsWithRequiredOperators(ddb, locator))),
          Promise.all(availableUnits.map(locator => getToolsWithPmDates(ddb, getLocatorUnit(locator)))),
        ]);
        
        const toolPmMap = allData[6].flat().reduce((map, cur) => {
          if(cur.lastPmDate && cur.pmCycle)
            map.set(cur.toolNumber, { lastPmDate: cur.lastPmDate, pmCycle: cur.pmCycle, cycleCount: cur.cycleCount });
          return map;
        }, new Map());
        const toolLocationMap = allData[6].flat().reduce((map, cur) => {
          if(cur.eamLocation)
            map.set(cur.toolNumber, cur.eamLocation);
          return map;
        }, new Map());
        const assemblyToolMap = allData[5].flat().reduce((map, cur) => {
          map.set(`${cur.toolNumber}|${cur.partNumber}`, cur.requiredOperators);
          return map;
        }, new Map());
        const allJobs = [...allData[1].flat(), ...allData[4]];
        const machineData = allData[0].flat().map(machineObj => {
          const jobs = allJobs.filter(jobObj => jobObj.assignedTo === machineObj.serialNumber &&
            jobObj.stdTotalRunTime && (jobObj.actualEndDate == null || jobObj.actualEndDate === 0))
            .map(jobObj => {
              const requiredOperators = assemblyToolMap.get(`${jobObj.toolNumber}|${jobObj.partNumber}`);
              if(requiredOperators) jobObj.requiredOperators = requiredOperators;
              const toolPmData = toolPmMap.get(jobObj.toolNumber);
              const toolLocation = toolLocationMap.get(jobObj.toolNumber);
              if(toolPmData) {
                jobObj.lastPmDate = toolPmData.lastPmDate;
                jobObj.pmCycle = toolPmData.pmCycle;
                jobObj.cycleCount = toolPmData.cycleCount;
              }
              if(toolLocation) jobObj.toolLocation = toolLocation;
              return jobObj;
            })
            .reduce((jobPairArr, currentRecord) => {
              let sisterJobArrIndex = jobPairArr.findIndex(jobRecordArr => jobRecordArr.length === 1 &&
                jobRecordArr[0].partNumber === currentRecord.sisterPartNumber &&
                jobRecordArr[0].sisterPartNumber === currentRecord.partNumber &&
                jobRecordArr[0].startQty === currentRecord.startQty &&
                jobRecordArr[0].toolNumber === currentRecord.toolNumber &&
                Math.abs(jobRecordArr[0].scheduledStartDate - currentRecord.scheduledStartDate) < 3600000);
              if(sisterJobArrIndex >= 0) jobPairArr[sisterJobArrIndex].push(currentRecord);
              else jobPairArr.push([currentRecord]);
              return jobPairArr;
            }, []);
          const standards = allData[2].find(locationObj => locationObj.locationId === machineObj.machineLocator);
          const schedule = adjustSchedule(allData[3].flat().filter(scheduleObj => scheduleObj.machineSerialNumber === machineObj.serialNumber)
            .filter(scheduleObj => scheduleObj.startTime <= scheduleObj.endTime), jobs, standards, initTime);
          const releasedJobs = cloneDeep(jobs.filter(jobArr =>
            !jobArr.some(jobObj =>
              schedule.some(scheduleObj => scheduleObj.jobNumbers &&
                scheduleObj.jobNumbers.includes(jobObj.jobNumber)))))
            .sort((a, b) => cmp(a[0].scheduledStartDate, b[0].scheduledStartDate));
          return {
            ...machineObj,
            standards,
            jobs,
            schedule,
            newSchedule: cloneDeep(schedule),
            releasedJobs,
            newReleasedJobs: cloneDeep(releasedJobs),
          };
        }).sort((a, b) =>
          cmp(
            a.machineLocator !== 'NAM-CNL-U20CWH' ? a.commonName.padStart(5, '0') : a.commonName,
            b.machineLocator !== 'NAM-CNL-U20CWH' ? b.commonName.padStart(5, '0') : b.commonName,
          ));
        const locationDataMap = allData[2].reduce((map, cur) => {
          map.set(cur.locationId, cur);
          return map;
        }, new Map());
        // console.log('authorizedModules:', authorizedModules);
        // console.log('availableUnits:', availableUnits);
        // console.log('machines:', allData[0]);
        // console.log('jobs:', allJobs);
        // console.log('standards:', allData[2]);
        // console.log('schedule rows:', allData[3]);
        // console.log('machineData:', machineData);
        updateLocators(['ALL', ...availableUnits]);
        updateMachineData(machineData);
        updateLocationData(locationDataMap);
        setTimeNow(initTime);
      }
      catch(e) {
        console.error('initData error ->', e);
      }
      finally {
        updateLoadingMessage(null);
      }
    };
    initData();
  }, [
    authorizedModules,
    credentials,
    updateMachineData,
    updateLocators,
    updateLoadingMessage,
    updateLocationData,
  ]);
  
  const filteredMachineJobData = machineData != null ?
    cloneDeep(machineData).filter(machineObj => locatorFilter === 'ALL' || machineObj.machineLocator === locatorFilter)
      .filter(machineObj => {
        if(!timelineTextFilter.length) return true;
        const jobData = machineObj.schedule.map(s => s.jobNumbers ?
          machineObj.jobs.find(jobArr => jobArr.some(job => job.jobNumber === s.jobNumbers[0])) :
          [],
        ).filter(o => o != null);
        if(timelineViewType === 'PART') {
          const partNumbers = jobData.map(jobArr => jobArr.map(j => j.partNumber).join(' & '));
          return partNumbers.some(partNumber => partNumber.toLowerCase().includes(timelineTextFilter.toLowerCase().trim()));
        }
        if(timelineViewType === 'JOB') {
          const jobNumbers = jobData.map(jobArr => jobArr.map(j => j.jobNumber).join(' & '));
          return jobNumbers.some(jobNumber => jobNumber.toLowerCase().includes(timelineTextFilter.toLowerCase().trim()));
        }
        if(timelineViewType === 'TOOL') {
          const toolNumbers = jobData.map(jobArr => jobArr.map(j => j.toolNumber).join(' & '));
          return toolNumbers.some(toolNumber => toolNumber.toLowerCase().includes(timelineTextFilter.toLowerCase().trim()));
        }
        if(timelineViewType === 'MATERIAL') {
          const materialNumbers = jobData.map(jobArr => jobArr.map(j => j.partMaterials.map(m => m.materialNumber)).flat().join(' & '));
          return materialNumbers.some(materialNumber => materialNumber.toLowerCase().includes(timelineTextFilter.toLowerCase().trim()));
        }
        if(timelineViewType === 'PACKAGING') {
          const packagingNumbers = jobData.map(jobArr => jobArr.map(j => j.containerPartNumber)).flat().filter(o => o != null);
          return packagingNumbers.some(packagingNumber => packagingNumber.toLowerCase().includes(timelineTextFilter.toLowerCase().trim()));
        }
        return true;
      })
      .map(machineObj => {
        if(weekFilter === 'NONE') {
          machineObj.filteredSchedule = null;
        }
        else if(weekFilter === 'THIS WEEK') {
          const { startTime, endTime } = getThisWeekStartEnd(timeNow, machineObj.standards.timeZone);
          machineObj.filteredSchedule = machineObj.schedule.filter(o => o.startTime >= startTime && o.startTime < endTime);
        }
        else if(weekFilter === 'NEXT WEEK') {
          const { startTime, endTime } = getNextWeekStartEnd(timeNow, machineObj.standards.timeZone);
          machineObj.filteredSchedule = machineObj.schedule.filter(o => o.startTime >= startTime && o.startTime < endTime);
        }
        return machineObj;
      }) :
    [];
  
  const handleKeyDown = key => {
    if(key === 'ctrl+i') {
      if(timelinePixelsPerHour < 150) setTimelinePixelsPerHour(timelinePixelsPerHour + 5);
      return;
    }
    if(key === 'ctrl+o') {
      if(timelinePixelsPerHour > 5) setTimelinePixelsPerHour(timelinePixelsPerHour - 5);
      return;
    }
  };
  
  const containerWidth = Math.max(getTimelineMaxWidth(filteredMachineJobData, timelinePixelsPerHour) + 90, windowDimensions.width - scrollbarWidth);
  const curLocationData = locationData.get(locatorFilter);
  const curShiftData = curLocationData ? curLocationData.shiftTimes : null;
  const curShiftStartTime = curLocationData ?
    weekFilter === 'NEXT WEEK' ?
      getNextWeekStartEnd(timeNow, curLocationData.timeZone).startTime :
      timeNow :
    timeNow;
  
  return (
    <div>
      <Toolbar logout={logout}/>
      <div
        style={{
          ...styles.timelineContainer,
          width: containerWidth,
          backgroundColor: GLOBAL_STYLES[colorScheme].bgColor,
        }}
      >
        {filteredMachineJobData.map((machineObj, index, array) => (
          <Timeline
            machineObj={machineObj}
            key={`TIMELINE-${machineObj.serialNumber}`}
            pixelsPerHour={timelinePixelsPerHour}
            isLastRow={index === array.length - 1}
          />
        ))}
      </div>
      
      <TimelineLabel
        containerWidth={containerWidth}
        startTime={curShiftStartTime}
        pixelsPerHour={timelinePixelsPerHour}
        shiftsArr={curShiftData}
      />
      
      <KeyboardEventHandler
        handleKeys={['ctrl+i', 'ctrl+o', 'ctrl']}
        onKeyEvent={handleKeyDown}
      />
      
      <EditSchedule
        timeNow={timeNow}
        containerWidth={containerWidth}
      />
    </div>
  );
};

const styles = {
  timelineContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    paddingBottom: 60,
    paddingTop: 89,
  },
};

export default connect(mapStateToProps, mapDispatchToProps)(App);
